import { httpsCallable, type Functions } from 'firebase/functions';

export interface CallableResult<T> {
  data: T;
}

export type Callable = (payload: unknown) => Promise<CallableResult<unknown>>;

export interface DispatchOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

export class DispatchClient {
  constructor(private readonly callable: Callable, private readonly publishCallable?: Callable) {}

  async call<T = unknown>(command: string, params: unknown = {}, options: DispatchOptions = {}): Promise<T> {
    if (!command.trim()) throw new Error('A command name is required.');
    const timeoutMs = options.timeoutMs ?? (command === 'publishRig' ? 540_000 : 20_000);
    const controller = new AbortController();
    const timeout = globalThis.setTimeout(() => controller.abort(new Error('The cloud request timed out.')), timeoutMs);
    const abort = () => controller.abort(options.signal?.reason);
    options.signal?.addEventListener('abort', abort, { once: true });
    try {
      if (command === 'publishRig' && !this.publishCallable) {
        throw new Error('The publishRig callable is unavailable.');
      }
      const request = command === 'publishRig'
        ? this.publishCallable!(params ?? {})
        : this.callable({ command, params: params ?? {} });
      const aborted = new Promise<never>((_, reject) => {
        controller.signal.addEventListener(
          'abort',
          () => reject(controller.signal.reason ?? new Error('The cloud request was cancelled.')),
          { once: true },
        );
      });
      const result = await Promise.race([request, aborted]);
      return result.data as T;
    } finally {
      globalThis.clearTimeout(timeout);
      options.signal?.removeEventListener('abort', abort);
    }
  }
}

export function createDispatchClient(instance: Functions): DispatchClient {
  const callable = httpsCallable(instance, 'dispatch');
  const publish = httpsCallable(instance, 'publishRig', { timeout: 540_000 });
  return new DispatchClient(
    (payload) => callable(payload) as Promise<CallableResult<unknown>>,
    (payload) => publish(payload) as Promise<CallableResult<unknown>>,
  );
}
